import React from "react";
import styled, { css, useTheme } from "styled-components";
import { GroupData, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import GroupImage from "common/Images/GroupImage";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import PanelEmpty from "common/Empty/PanelEmpty.tsx";
import { useGroupSubscriptionData } from "features/settings/common/hooks";

interface GroupRowProps {
  groupData: GroupData;
  isExpired?: boolean;
  onCancelAutoRenewal: (groupData: GroupData) => void;
}

/** One group subscription: avatar, name, renewal / expiry, one action. */
const GroupRow = ({
  groupData,
  isExpired,
  onCancelAutoRenewal,
}: GroupRowProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { actionButton, expirationText, freeTrialModal } =
    useGroupSubscriptionData(
      groupData,
      () => onCancelAutoRenewal(groupData),
      t("group_subscriptions.cancel_auto_renewal")
    );
  const isCancel = !!groupData.next_auto_renewal;

  return (
    <Row $isExpired={isExpired}>
      <Avatar>
        <GroupImage src={groupData.image} $size={40} alt="" />
      </Avatar>
      <Texts>
        <Name>{groupData.name}</Name>
        <Meta>{expirationText}</Meta>
      </Texts>
      {actionButton && (
        <Action type="button" $quiet={isCancel} onClick={actionButton.onClick}>
          {actionButton.text}
        </Action>
      )}
      {freeTrialModal}
    </Row>
  );
};

interface NewLookGroupSubscriptionsProps {
  activeGroups: GroupData[];
  expiredGroups: GroupData[];
  isLoading: boolean;
  isFetching: boolean;
  onCancelAutoRenewal: (groupData: GroupData) => void;
}

/**
 * The Groups billing tab in the new look: active subscriptions as a list of
 * rows (instead of a 3-column grid of boxed cards), expired ones below.
 */
const NewLookGroupSubscriptions = ({
  activeGroups,
  expiredGroups,
  isLoading,
  isFetching,
  onCancelAutoRenewal,
}: NewLookGroupSubscriptionsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const theme = useTheme();
  const isEmpty = !activeGroups.length && !expiredGroups.length;

  return (
    <Panel
      title={t("group_subscriptions.title")}
      count={activeGroups.length || undefined}
      flush
    >
      <LoadingSpinnerOverlay
        isLoading={isFetching && !isLoading}
        backgroundColor={theme.colors.cardBgColor}
      >
        {isLoading ? (
          <List aria-busy={true}>
            {[160, 120, 140].map((width) => (
              <Row key={width}>
                <Skeleton width={32} height={32} radius={16} />
                <Texts>
                  <Skeleton width={width} height={14} />
                  <Skeleton width={90} height={12} />
                </Texts>
              </Row>
            ))}
          </List>
        ) : isEmpty ? (
          <PanelEmpty
            title={t("group_subscriptions.empty_title")}
            description={t("group_subscriptions.empty_description")}
          />
        ) : (
          <>
            <List>
              {activeGroups.map((groupData) => (
                <GroupRow
                  key={groupData.id}
                  groupData={groupData}
                  onCancelAutoRenewal={onCancelAutoRenewal}
                />
              ))}
            </List>
            {!!expiredGroups.length && (
              <>
                <SubTitle $first={!activeGroups.length}>
                  {t("group_subscriptions.expired_title")}
                </SubTitle>
                <List>
                  {expiredGroups.map((groupData) => (
                    <GroupRow
                      key={groupData.id}
                      groupData={groupData}
                      isExpired={true}
                      onCancelAutoRenewal={onCancelAutoRenewal}
                    />
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </LoadingSpinnerOverlay>
    </Panel>
  );
};

export default NewLookGroupSubscriptions;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;
const Row = styled.div<{ $isExpired?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 60px;
  padding: 8px 20px;
  /* Phones: the action moves under the name so names aren't cut short. */
  @media (max-width: 560px) {
    display: grid;
    grid-template-columns: 32px minmax(0, 1fr);
    column-gap: 12px;
    row-gap: 6px;
    padding: 10px 20px;
    > button {
      grid-column: 2;
      justify-self: start;
    }
  }
  &:not(:last-child) {
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  ${(p) =>
    p.$isExpired &&
    css`
      img {
        filter: grayscale(1);
        opacity: 0.7;
      }
    `}
`;
const Avatar = styled.div`
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  img {
    width: 32px;
    height: 32px;
    object-fit: cover;
  }
`;
const Texts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
`;
const Name = styled.div`
  font-size: 14px;
  line-height: 18px;
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Meta = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Action = styled.button<{ $quiet?: boolean }>`
  flex: none;
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid ${(p) => getNewLookTokens(p.theme).hairlineStrong};
  background: ${(p) => getNewLookTokens(p.theme).panel};
  color: ${(p) => getNewLookTokens(p.theme).text};
  font-family: inherit;
  ${newLookType.label};
  font-weight: 500;
  cursor: pointer;
  transition: border-color 120ms, color 120ms;
  &:hover {
    border-color: ${(p) => getNewLookTokens(p.theme).accent};
    color: ${(p) => getNewLookTokens(p.theme).accent};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
  }
  ${(p) =>
    p.$quiet &&
    css`
      /* A quiet text action, red only while it is pointed at. */
      height: 28px;
      padding: 0 4px;
      border-color: transparent;
      border-radius: ${NEW_LOOK_RADIUS.control}px;
      background: transparent;
      font-weight: 500;
      color: ${getNewLookTokens(p.theme).secondary};
      &:hover {
        border-color: transparent;
        color: ${getNewLookTokens(p.theme).down};
      }
    `}
`;
/** The expired list's micro label, on a hairline across the panel. */
const SubTitle = styled.div<{ $first?: boolean }>`
  padding: 16px 20px 6px;
  ${(p) =>
    !p.$first &&
    css`
      border-top: 1px solid ${getNewLookTokens(p.theme).hairline};
    `}
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
